import { useEffect, useState } from "react";
import {
  FaArrowUp,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPhone,
  FaTwitter,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import logo from "../assest/logo.png";
import SummaryApi from "../common";
import {
  APP_ADDRESS,
  APP_DESCRIPTION,
  APP_EMAIL,
  APP_NAME,
  APP_PHONE,
  APP_SHORT_NAME,
  COPYRIGHT_TEXT,
  FOOTER_CATEGORY_LIMIT,
  FOOTER_SUPPORT_LINKS,
  NEWSLETTER_BTN_TEXT,
  NEWSLETTER_PLACEHOLDER,
  ROUTES,
  SOCIAL_LINKS,
} from "../constants";
import { request } from "../helpers/request";

const SOCIAL_ICONS = [
  { Icon: FaFacebook, href: SOCIAL_LINKS.facebook, label: "Facebook" },
  { Icon: FaInstagram, href: SOCIAL_LINKS.instagram, label: "Instagram" },
  { Icon: FaTwitter, href: SOCIAL_LINKS.twitter, label: "Twitter" },
  { Icon: FaLinkedin, href: SOCIAL_LINKS.linkedin, label: "LinkedIn" },
];

const Footer = () => {
  const [categoryProduct, setCategoryProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const fetchCategoryProduct = async () => {
    setLoading(true);
    try {
      const response = await request(SummaryApi.categoryProduct);
      const dataResponse = await response.json();
      if (dataResponse.success) {
        setCategoryProduct(dataResponse.data.slice(0, FOOTER_CATEGORY_LIMIT));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryProduct();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-ds-inverse-surface text-white border-t-4 border-ds-primary pb-16 lg:pb-0">
      {/* Main footer grid */}
      <div className="max-w-[1280px] mx-auto px-10 pt-14 md:pt-16 pb-10 md:pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-12 md:mb-16">
          {/* Brand */}
          <div className="flex flex-col gap-5 lg:col-span-1">
            <Link
              to={ROUTES.HOME}
              className="flex items-center gap-3 group w-fit"
            >
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-all">
                <img
                  src={logo}
                  alt={APP_NAME}
                  className="w-7 h-7 brightness-0 invert object-contain"
                />
              </div>
              <span className="font-barlow font-bold text-[22px] tracking-tight text-ds-primary uppercase">
                {APP_SHORT_NAME}
              </span>
            </Link>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-xs">
              {APP_DESCRIPTION}
            </p>

            {/* Contact info */}
            <div className="flex flex-col gap-2 text-sm">
              <a
                href={`tel:${APP_PHONE}`}
                className="flex items-center gap-2.5 text-neutral-400 hover:text-white transition-colors group"
              >
                <FaPhone className="text-xs flex-shrink-0 group-hover:text-accent transition-colors" />
                {APP_PHONE}
              </a>
              <a
                href={`mailto:${APP_EMAIL}`}
                className="flex items-center gap-2.5 text-neutral-400 hover:text-white transition-colors group"
              >
                <FaEnvelope className="text-xs flex-shrink-0 group-hover:text-accent transition-colors" />
                {APP_EMAIL}
              </a>
              <span className="flex items-center gap-2.5 text-neutral-400">
                <FaMapMarkerAlt className="text-xs flex-shrink-0" />
                {APP_ADDRESS}
              </span>
            </div>

            {/* Social Links */}
            <div className="flex gap-2.5">
              {SOCIAL_ICONS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-neutral-400 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all"
                >
                  <Icon className="text-sm" />
                </a>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-inter font-semibold text-[11px] uppercase tracking-[0.08em] text-white mb-5">
              Shop By Category
            </h4>
            <ul className="flex flex-col gap-2.5">
              {loading
                ? [1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-4 bg-white/5 rounded-lg w-28 shimmer"
                    />
                  ))
                : categoryProduct.map((product) => (
                    <li key={product.category}>
                      <Link
                        to={`${ROUTES.PRODUCT_CATEGORY}?category=${product.category}`}
                        className="text-neutral-400 hover:text-white transition-colors text-sm capitalize flex items-center gap-2 group"
                      >
                        <span className="w-1 h-1 bg-accent rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        {product.category}
                      </Link>
                    </li>
                  ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-inter font-semibold text-[11px] uppercase tracking-[0.08em] text-white mb-5">
              Support
            </h4>
            <ul className="flex flex-col gap-2.5">
              {FOOTER_SUPPORT_LINKS.map(({ label, path }) => (
                <li key={label}>
                  <Link
                    to={path}
                    className="text-neutral-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-accent rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="flex flex-col gap-4">
            <h4 className="font-inter font-semibold text-[11px] uppercase tracking-[0.08em] text-white">
              Newsletter
            </h4>
            <p className="text-neutral-400 text-sm">
              Get the latest gear updates and exclusive offers.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setEmail("");
              }}
              className="flex flex-col gap-2"
            >
              <input
                type="email"
                placeholder={NEWSLETTER_PLACEHOLDER}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-sm outline-none focus:border-white/25 transition-all text-white placeholder:text-neutral-500"
                required
              />
              <button
                type="submit"
                className="h-11 bg-ds-primary text-white text-sm font-inter font-semibold rounded-full hover:bg-ds-primary-hover transition-all active:scale-95"
              >
                {NEWSLETTER_BTN_TEXT}
              </button>
            </form>

            {/* Trust badges */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center gap-1.5 text-neutral-500 text-xs">
                <span className="bg-white/10 text-white text-[9px] font-black px-1.5 py-0.5 rounded">
                  SSL
                </span>
                Secure
              </div>
              <div className="w-px h-4 bg-white/10" />
              <div className="flex items-center gap-1.5 text-neutral-500 text-xs">
                <span className="bg-[#635bff]/20 text-[#635bff] text-[9px] font-black px-1.5 py-0.5 rounded border border-[#635bff]/30">
                  Stripe
                </span>
                Payments
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-neutral-500 text-xs text-center sm:text-left">
            {COPYRIGHT_TEXT}
          </p>

          <button
            onClick={scrollToTop}
            aria-label="Back to top"
            className="flex items-center gap-2 text-xs font-semibold text-neutral-500 hover:text-white transition-colors group"
          >
            Back to top
            <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-all">
              <FaArrowUp className="text-xs group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile spacer for bottom nav */}
      <div className="h-16 lg:hidden" />
    </footer>
  );
};

export default Footer;
